import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { ArrowLeft, Calendar, Users, MapPin, Clock, Plane, Train, Bus, Bed, Hotel, Star, Quote, Youtube, Instagram, Image as ImageIcon, Video, FileText, Play, User } from 'lucide-react'
import { AboutSection } from '../components/TourDetails/AboutSection'
import { BookingSection } from '../components/TourDetails/BookingSection'
import { JourneyMemories } from '../components/TourDetails/JourneyMemories'
import { CompleteTourImage } from '../components/TourDetails/CompleteTourImage'
import { FullScreenImageModal } from '../components/TourDetails/FullScreenImageModal'
import { useTours, type Tour, getLocalizedText, getLocalizedArray, getLocalizedNumber } from '../hooks/useTours'
import { calculateTourStatus, getStatusLabel } from '../utils/tourStatus'
import { useHotels, getHotelsByCity, getLocalizedText as getHotelLocalizedText } from '../hooks/useHotels'
import { useTestimonials } from '../hooks/useTestimonials'


// Tours data is now loaded from external JSON file

export function TourDetail() {
  const { t, i18n } = useTranslation()
  const { isRTL, language } = useLanguage()
  const { id } = useParams()
  const [currentSegment, setCurrentSegment] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { tours, loading, error } = useTours()
  const { hotels } = useHotels()
  const { testimonials } = useTestimonials()
  const currentLanguage = i18n.language
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  })

  // Find tour by id from URL parameter
  const tour = tours.find(t => t.id === id)

  // Get current segment for ongoing tours
  const getCurrentSegmentIndex = () => {
    if (!tour || calculateTourStatus(tour.departureDate, tour.returnDate) !== 'ongoing') return -1
    
    // For ongoing tours, we'll show the first segment as current
    // since the database route structure doesn't include dates
    return 0
  }

  // Scroll to top when tour changes, or to current segment for ongoing tours
  useEffect(() => {
    if (tour && calculateTourStatus(tour.departureDate, tour.returnDate) === 'ongoing') {
      const currentIndex = getCurrentSegmentIndex()
      
      if (currentIndex !== -1) {
        // Scroll to current segment after a short delay to ensure page is loaded
        setTimeout(() => {
          const segmentElement = document.querySelector(`[data-segment-index="${currentIndex}"]`)
          if (segmentElement) {
            segmentElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: isRTL ? 'start' : 'start'
            })
          }
        }, 500)
      } else {
        // If no current segment found, scroll to top
        window.scrollTo(0, 0)
      }
    } else {
      // For non-ongoing tours, just scroll to top
      window.scrollTo(0, 0)
    }
  }, [id, tour])

  useEffect(() => {
    if (!tour) return
    
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      const segmentIndex = Math.floor(latest * tour.route.length)
      setCurrentSegment(Math.min(segmentIndex, tour.route.length - 1))
    })

    return unsubscribe
  }, [scrollYProgress, tour])

  const getVehicleIcon = (vehicle: string) => {
    switch (vehicle) {
      case 'airplane':
        return <Plane className="h-6 w-6" />
      case 'train':
        return <Train className="h-6 w-6" />
      case 'bus':
        return <Bus className="h-6 w-6" />
      default:
        return <MapPin className="h-6 w-6" />
    }
  }

  const getVehicleColor = (vehicle: string) => {
    switch (vehicle) {
      case 'airplane':
        return 'text-blue-500'
      case 'train':
        return 'text-primary'
      case 'bus':
        return 'text-orange-500'
      default:
        return 'text-primary'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing':
        return 'bg-primary text-white'
      case 'upcoming':
        return 'bg-blue-500 text-white'
      case 'past':
        return 'bg-gray-500 text-white'
    }
  }

  const getYouTubeId = (url: string) => {
    // Handle YouTube Shorts URLs (youtube.com/shorts/VIDEO_ID)
    if (url.includes('/shorts/')) {
      const shortsMatch = url.match(/\/shorts\/([^#&?\/]*)/)
      if (shortsMatch && shortsMatch[1].length === 11) {
        return shortsMatch[1]
      }
    }
    // Handle regular YouTube URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return match && match[2].length === 11 ? match[2] : url
  }

  const getYouTubeThumbnail = (videoId: string) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  }

  const renderTestimonialContent = (testimonial: any) => {
    switch (testimonial.type) {
      case 'youtube':
        const videoId = getYouTubeId(testimonial.content)
        const isPlaying = playingVideo === testimonial.id
        
        if (isPlaying) {
          return (
            <div className="w-full aspect-video rounded-t-2xl overflow-hidden relative">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ display: 'block' }}
              />
            </div>
          )
        }
        
        return (
          <div 
            className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
            onClick={() => setPlayingVideo(testimonial.id)}
          >
            <img
              src={getYouTubeThumbnail(videoId)}
              alt="YouTube video thumbnail"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              }}
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="h-10 w-10 text-white ml-1" fill="white" />
              </motion.div>
            </div>
          </div>
        )
      
      case 'image':
        return (
          <div className="relative w-full aspect-video rounded-t-2xl overflow-hidden group">
            <img
              src={testimonial.content}
              alt={testimonial.author_name || 'Testimonial'}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          </div>
        )
      
      case 'video':
        const isVideoPlaying = playingVideo === testimonial.id
        if (isVideoPlaying) {
          return (
            <div className="w-full aspect-video rounded-t-2xl overflow-hidden relative">
              <video
                src={testimonial.content}
                controls
                autoPlay
                className="absolute inset-0 w-full h-full object-cover"
                style={{ display: 'block' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )
        }
        return (
          <div 
            className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
            onClick={() => setPlayingVideo(testimonial.id)}
          >
            <video
              src={testimonial.content}
              className="w-full h-full object-cover"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-20 h-20 bg-primary rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="h-10 w-10 text-white ml-1" fill="white" />
              </motion.div>
            </div>
          </div>
        )
      
      default: // text
        return (
          <div className="relative min-h-[200px] p-4">
            <Quote className="absolute top-2 left-2 h-12 w-12 text-primary/20" />
            <div className="text-lg text-foreground leading-relaxed relative z-10 pt-8" dir={isRTL ? 'rtl' : 'ltr'}>
              {testimonial.content}
            </div>
          </div>
        )
    }
  }

  const currentSegmentIndex = getCurrentSegmentIndex()
  
  // Get random testimonial for display
  const featuredTestimonial = testimonials.length > 0 
    ? testimonials[Math.floor(Math.random() * testimonials.length)] 
    : null

  // Handle loading state
  if (loading) {
    return (
      <div className="pt-16 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tour details...</p>
        </div>
      </div>
    )
  }

  // Handle error state
  if (error) {
    return (
      <div className="pt-16 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-destructive mb-4">Error loading tour: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {t('home.retry')}
          </button>
        </div>
      </div>
    )
  }

  // Handle tour not found
  if (!tour) {
    return (
      <div className="pt-16 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Tour not found</p>
          <Link 
            to="/" 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {t('home.backToTours')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-16">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('home.backToTours')}</span>
              </motion.button>
            </Link>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(calculateTourStatus(tour.departureDate, tour.returnDate))}`}>
              {getStatusLabel(calculateTourStatus(tour.departureDate, tour.returnDate))}
            </div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {getLocalizedText(tour.title, currentLanguage)}
          </motion.h1>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Duration</div>
                <div className="font-medium duration">{tour.duration}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Route</div>
                <div className="font-medium">{tour.route.length} segments</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold text-primary price">{getLocalizedNumber(tour.price, language)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Journey Visualization - For all tours */}
      {tour.route && tour.route.length > 0 && (
        <div ref={containerRef} className="py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">{t('home.journeyVisualization')}</h2>
              <p className="text-muted-foreground">
                {calculateTourStatus(tour.departureDate, tour.returnDate) === 'ongoing' 
                  ? t('home.scrollToSeeProgress') 
                  : calculateTourStatus(tour.departureDate, tour.returnDate) === 'upcoming'
                  ? t('home.previewJourneyRoute')
                  : 'Tour Route Overview'
                }
              </p>
            </div>

            {/* Progress Bar - Only for ongoing tours */}
            {calculateTourStatus(tour.departureDate, tour.returnDate) === 'ongoing' && (
              <div className="mb-12">
                <div className="relative h-2 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 via-primary to-orange-500 rounded-full"
                    style={{ width: `${scrollYProgress.get() * 100}%` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${scrollYProgress.get() * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className={`flex justify-between mt-2 text-sm text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span>{t('home.start')}</span>
                  <span>{Math.round(scrollYProgress.get() * 100)}% {t('home.complete')}</span>
                  <span>{t('home.end')}</span>
                </div>
              </div>
            )}

            {/* Journey Segments */}
            <div className="space-y-8">
              {tour.route.map((segment, index) => {
                const tourStatus = calculateTourStatus(tour.departureDate, tour.returnDate)
                const isActive = tourStatus === 'ongoing' && currentSegment === index
                const isCompleted = tourStatus === 'ongoing' && currentSegment > index || tourStatus === 'past'
                const isCurrentDay = tourStatus === 'ongoing' && currentSegmentIndex === index
                
                return (
                  <motion.div
                    key={index}
                    data-segment-index={index}
                    className={`relative p-8 rounded-2xl border transition-all duration-500 ${
                      isCurrentDay
                        ? 'bg-primary/20 border-primary shadow-xl scale-105 ring-2 ring-primary/30'
                        : isActive
                        ? 'bg-primary/10 border-primary shadow-lg scale-105'
                        : isCompleted
                        ? 'bg-primary/10 border-primary'
                        : 'bg-card border-border'
                    }`}
                    initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    animate={isCurrentDay ? { 
                      boxShadow: [
                        '0 0 0 0 rgba(128, 0, 0, 0.4)',
                        '0 0 0 10px rgba(128, 0, 0, 0.1)',
                        '0 0 0 0 rgba(128, 0, 0, 0.4)'
                      ]
                    } : {}}
                  >
                    <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {/* Current Day Indicator */}
                      {isCurrentDay && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className={`absolute -top-2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                            isRTL ? '-left-2' : '-right-2'
                          }`}
                        >
                          TODAY
                        </motion.div>
                      )}
                      
                      {/* Vehicle Icon */}
                      <motion.div
                        className={`p-4 rounded-full ${
                          isCurrentDay 
                            ? 'bg-primary text-white shadow-lg' 
                            : isActive 
                            ? 'bg-primary text-white' 
                            : 'bg-secondary text-foreground'
                        }`}
                        animate={isCurrentDay ? { 
                          scale: [1, 1.1, 1],
                          transition: { duration: 2, repeat: Infinity }
                        } : isActive ? { 
                          scale: [1, 1.1, 1] 
                        } : {}}
                        transition={{ duration: 2, repeat: (isCurrentDay || isActive) ? Infinity : 0 }}
                      >
                        <div className={isCurrentDay || isActive ? 'text-white' : getVehicleColor(segment.vehicle.en)}>
                          {getVehicleIcon(segment.vehicle.en)}
                        </div>
                      </motion.div>

                      {/* Segment Info */}
                      <div className="flex-1">
                        <div className={`flex items-center gap-4 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <h3 className="text-xl font-bold text-foreground" dir={isRTL ? 'rtl' : 'ltr'}>
                            {getLocalizedText(segment.from, currentLanguage)} {isRTL ? '←' : '→'} {getLocalizedText(segment.to, currentLanguage)}
                            {isCurrentDay && (
                              <motion.span
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-primary font-normal`}
                              >
                                (Current Location)
                              </motion.span>
                            )}
                          </h3>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            isCurrentDay 
                              ? 'bg-primary text-primary-foreground shadow-md' 
                              : isActive 
                              ? 'bg-primary text-primary-foreground' 
                              : isCompleted 
                              ? 'bg-primary text-white' 
                              : 'bg-secondary text-foreground'
                          }`} dir={isRTL ? 'rtl' : 'ltr'}>
                            {segment.vehicle.en === 'airplane' ? 'Flight' :
                             segment.vehicle.en === 'train' ? 'Train' : 'Bus'}
                          </div>
                        </div>
                        
                        <div className={`grid gap-4 text-sm ${segment.duration && segment.distance ? 'md:grid-cols-3' : segment.duration || segment.distance ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="date">{new Date(segment.date).toLocaleDateString()}</span>
                          </div>
                          {segment.duration && (
                            <div className="flex items-center gap-2">
                              <Bed className="h-4 w-4 text-muted-foreground" />
                              <span className="duration">{segment.duration}</span>
                            </div>
                          )}
                          {segment.distance && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="distance">{segment.distance.replace(/\s*km\s*/gi, '')}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Indicator */}
                      {isActive && (
                        <motion.div
                          className="w-3 h-3 bg-primary rounded-full"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        />
                      )}
                    </div>

                    {/* Hotels - Only show if hotels are attached to this segment */}
                    {(() => {
                      // Collect all attached hotels for this segment (only "to" destination)
                      const attachedHotels: any[] = []
                      
                      // Only check for hotel in "to" city
                      if (segment.hotelIdTo) {
                        const toHotel = hotels.find(h => h.id === segment.hotelIdTo)
                        if (toHotel) {
                          attachedHotels.push({
                            hotel: toHotel,
                            city: getLocalizedText(segment.to, currentLanguage),
                            cityType: 'to'
                          })
                        }
                      }
                      
                      // Only show hotels section if there are attached hotels
                      if (attachedHotels.length === 0) {
                        return null
                      }
                      
                      return (
                        <div className="mt-6 pt-6 border-t border-border">
                          <div className="flex items-center gap-2 mb-4">
                            <Hotel className="h-5 w-5 text-primary" />
                            <h4 className="text-lg font-semibold text-foreground">
                              {t('tour.hotels', 'Hotels')}
                            </h4>
                          </div>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {attachedHotels.map((item, idx) => (
                              <Link
                                key={`${item.hotel.id}-${item.cityType}-${idx}`}
                                to={`/hotel/${item.hotel.id}`}
                                className="bg-secondary/50 border border-border rounded-lg p-4 hover:bg-secondary transition-colors group"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                      {getHotelLocalizedText(item.hotel.name, currentLanguage)}
                                    </h5>
                                    <p className="text-xs text-muted-foreground mt-1">{item.city}</p>
                                  </div>
                                  {item.hotel.rating && (
                                    <div className="flex items-center gap-1">
                                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                      <span className="text-xs font-medium">{item.hotel.rating.toFixed(1)}</span>
                                    </div>
                                  )}
                                </div>
                                {item.hotel.images && item.hotel.images.length > 0 && (
                                  <img
                                    src={item.hotel.images[0]}
                                    alt={getHotelLocalizedText(item.hotel.name, currentLanguage)}
                                    className="w-full h-32 object-cover rounded-lg mb-2"
                                  />
                                )}
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {getHotelLocalizedText(item.hotel.address, currentLanguage)}
                                </p>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )
                    })()}

                    {/* Connection Line */}
                    {index < tour.route.length - 1 && (
                      <div className={`absolute top-full w-0.5 h-8 bg-border ${
                        isRTL ? 'right-12' : 'left-12'
                      }`} />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tour Details */}
      <div className="py-12 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* About Section */}
            <AboutSection 
              description={getLocalizedText(tour.description, currentLanguage)} 
              highlights={getLocalizedArray(tour.highlights, currentLanguage)} 
            />

            {/* Right Column - Booking or Memories */}
            {calculateTourStatus(tour.departureDate, tour.returnDate) === 'past' ? (
              <JourneyMemories 
                memories={tour.memories || []} 
                onImageClick={setSelectedImage} 
              />
            ) : (
              <BookingSection
                status={calculateTourStatus(tour.departureDate, tour.returnDate)}
                price={tour.price}
                departureDate={tour.departureDate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      {featuredTestimonial && (
        <section className="py-12 bg-background">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">
                {t('home.testimonials.title', 'What Our Pilgrims Say')}
              </h2>
              <p className="text-muted-foreground">
                {t('home.testimonials.subtitle', 'Hear from those who have journeyed with us')}
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-2xl transition-all duration-300"
              >
                {/* Content */}
                <div className="relative w-full">
                  {renderTestimonialContent(featuredTestimonial)}
                  {/* Type Badge */}
                  <div className="absolute top-3 left-3 z-10">
                    <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-medium">
                      {featuredTestimonial.type === 'youtube' && <Youtube className="h-4 w-4" />}
                      {featuredTestimonial.type === 'instagram' && <Instagram className="h-4 w-4" />}
                      {featuredTestimonial.type === 'image' && <ImageIcon className="h-4 w-4" />}
                      {featuredTestimonial.type === 'video' && <Video className="h-4 w-4" />}
                      {featuredTestimonial.type === 'text' && <FileText className="h-4 w-4" />}
                      <span className="capitalize">{featuredTestimonial.type}</span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {featuredTestimonial.type === 'text' && (
                    <div className="mb-4">
                      <Quote className="h-8 w-8 text-primary/30 mb-2" />
                    </div>
                  )}
                  
                  {(featuredTestimonial.author_name || featuredTestimonial.author_location) && (
                    <div className="flex items-center gap-3 mt-4">
                      <div className="p-2 bg-primary/10 rounded-full">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        {featuredTestimonial.author_name && (
                          <div className="font-semibold text-foreground">
                            {featuredTestimonial.author_name}
                          </div>
                        )}
                        {featuredTestimonial.author_location && (
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {featuredTestimonial.author_location}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
              <div className="flex justify-end mt-3">
                <Link to="/testimonials">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-fit bg-secondary text-secondary-foreground py-3 px-6 rounded-lg font-semibold hover:bg-secondary/80 transition-colors border border-border"
                  >
                    See Testimonials
                  </motion.button>
                </Link>
              </div>
            </div>
            
          
          </div>
        </section>
      )}

      {/* Progress Bar for Past Tours */}
      {calculateTourStatus(tour.departureDate, tour.returnDate) === 'past' && (
        <section className="py-8 bg-secondary/20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('home.journeyCompleted')}</h2>
              <p className="text-muted-foreground">{t('home.pilgrimageCompleted')}</p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <div className="relative h-3 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2, delay: 0.5 }}
                />
              </div>
              <div className={`flex justify-between mt-3 text-sm text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('home.start')}</span>
                <span className="font-semibold text-primary">100% {t('home.complete')}</span>
                <span>{t('home.end')}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Complete Tour Image Section */}
      <CompleteTourImage
        image={tour.image}
        title={getLocalizedText(tour.title, currentLanguage)}
        duration={tour.duration}
        routeLength={tour.route.length}
        onImageClick={setSelectedImage}
      />

      {/* Full-Screen Image Modal */}
      <FullScreenImageModal
        selectedImage={selectedImage}
        tourTitle={getLocalizedText(tour.title, currentLanguage)}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  )
}
